import { lazy, type ComponentType } from "react";

/**
 * Wraps `React.lazy()` with a one-shot retry + hard reload on stale chunk
 * fetch errors.
 *
 * Why: na een nieuwe deploy verwijst de oude `index.html` (in de browser cache
 * of een open tab) nog naar chunk-bestanden die niet meer bestaan. Vite + de
 * browser falen dan met "Failed to fetch dynamically imported module" zodra
 * een route lazy geladen wordt. Dat wil je niet als hard error in de UI:
 * een simpele reload van de pagina pakt de nieuwe `index.html` met de juiste
 * chunk-hashes op.
 *
 * Strategie:
 *   1. Importeer de chunk normaal.
 *   2. Bij een chunk-load fout: doe één retry na 200ms (dekt korte
 *      netwerkhickups en CDN-propagatie).
 *   3. Werkt de retry ook niet en is dit nog niet eerder geprobeerd binnen
 *      deze pageload, sla een sessionStorage-vlag op en force-reload de
 *      pagina zodat de browser de nieuwe `index.html` ophaalt.
 *   4. Werkt het na de reload nog niet, gooi de fout door (zodat de
 *      ErrorBoundary hem kan tonen) — dit voorkomt een reload-loop.
 */
const RELOAD_FLAG_PREFIX = "lazy-retry:";

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message ?? "";
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module") ||
    error.name === "ChunkLoadError"
  );
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  chunkKey: string,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    const flagKey = `${RELOAD_FLAG_PREFIX}${chunkKey}`;
    try {
      return await factory();
    } catch (firstError) {
      if (!isChunkLoadError(firstError)) throw firstError;

      try {
        await new Promise((resolve) => setTimeout(resolve, 200));
        return await factory();
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) throw retryError;

        const alreadyReloaded =
          typeof sessionStorage !== "undefined" &&
          sessionStorage.getItem(flagKey) === "1";

        if (!alreadyReloaded && typeof window !== "undefined") {
          try {
            sessionStorage.setItem(flagKey, "1");
          } catch {
            // sessionStorage kan disabled zijn (private mode); reload toch.
          }
          window.location.reload();
          // Geef React een Promise die nooit resolved zodat de Suspense
          // fallback tijdens de reload zichtbaar blijft.
          return new Promise<{ default: T }>(() => {});
        }

        throw retryError;
      }
    }
  });
}

/**
 * Reset de reload-flag voor een chunk. Aanroepen wanneer een chunk wel
 * succesvol is geladen, zodat een latere stale-chunk fout opnieuw één
 * reload mag triggeren in dezelfde browsersessie.
 */
export function clearLazyRetryFlag(chunkKey: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(`${RELOAD_FLAG_PREFIX}${chunkKey}`);
  } catch {
    // negeer
  }
}
